import invariant from 'invariant';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import { H6 } from '#/components/Text/H6';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiTableOfContentsParent } from '#/modules/cms/api/types/CmsApiTableOfContents';
import { scrollIntoView } from '#/utils/scrollIntoView';

import {
  CmsTableOfContentsChildContainer,
  CmsTableOfContentsChildren,
  CmsTableOfContentsContainer,
  CmsTableOfContentsLink,
} from './CmsTableOfContents.styles';
import { useTableOfContentsChapterAnchor } from './useTableOfContentsChapterAnchor';

type Props = {
  parents: CmsApiTableOfContentsParent[];
};

export function CmsTableOfContents({ parents }: Props) {
  const { i18n } = useI18n();

  const [indicator, setIndicator] = useState({
    top: 0,
    height: 0,
  });

  const ref = useRef<HTMLDivElement>(null);

  const navigateToChapter = useCallback((slug: string) => {
    const el = document.getElementById(`${slug}`);

    if (el) {
      scrollIntoView(el);
    }
  }, []);

  const allChapterSlugs: string[] = [];
  parents.forEach((parent) => {
    allChapterSlugs.push(parent.url);
    parent.children.forEach((child) => {
      allChapterSlugs.push(child.url);
    });
  });

  const chapterAnchor = useTableOfContentsChapterAnchor(allChapterSlugs);

  useEffect(() => {
    const el = document.getElementById(`table-of-contents-${chapterAnchor}`);
    if (el) {
      const parentIndex = parents.findIndex(
        (parent) => parent.url === chapterAnchor,
      );

      let top = el.offsetTop + 7;
      if (parentIndex > 0) {
        const prevParentEl = document.getElementById(
          `table-of-contents-${parents[parentIndex - 1].url}`,
        );
        invariant(prevParentEl, 'should have parent el');
        top = prevParentEl.offsetTop + prevParentEl.offsetHeight + 7;
      }

      setIndicator({
        top,
        height: el.offsetHeight - 14,
      });
    }
  }, [chapterAnchor, parents]);

  return (
    <CmsTableOfContentsContainer
      $indicatorTop={indicator.top}
      $indicatorHeight={indicator.height}
      ref={ref}
    >
      <H6 mb="15px">{i18n('Table of Contents')}</H6>
      {parents.map((parent, i) => (
        <Fragment key={[i, parent.url].join()}>
          <CmsTableOfContentsLink
            onClick={() => navigateToChapter(parent.url)}
            $active={parent.url === chapterAnchor}
            id={`table-of-contents-${parent.url}`}
          >
            {parent.heading}
          </CmsTableOfContentsLink>
          <CmsTableOfContentsChildren
            $open={parent.children.some(
              (c) => c.url === chapterAnchor || parent.url === chapterAnchor,
            )}
            $numChildren={parent.children.length}
            className={`table-of-contents-children-${parent.url}`}
          >
            {parent.children.map((child) => (
              // TODO: Fix this from eslint v9 update
              // oxlint-disable-next-line react/jsx-key
              <CmsTableOfContentsChildContainer>
                <CmsTableOfContentsLink
                  onClick={() => navigateToChapter(child.url)}
                  $active={child.url === chapterAnchor}
                  id={`table-of-contents-${child.url}`}
                >
                  {child.heading}
                </CmsTableOfContentsLink>
              </CmsTableOfContentsChildContainer>
            ))}
          </CmsTableOfContentsChildren>
        </Fragment>
      ))}
    </CmsTableOfContentsContainer>
  );
}
